import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { PublicCall } from '../../types'
import { formatPrice } from '../../utils/marketPrice'
import { formatPnlPct } from '../../utils/pnl'
import { EXIT_REASON_LABEL, exitReason, fillMethodLabel } from '../../utils/signalPnl'
import { formatUtcSeconds } from '../../utils/utcTime'
import { VERIFY_PATH } from '../../routes'
import LevelExitsNote from '../LevelExitsNote'
import Phase2TrialNote from '../Phase2TrialNote'
import ResultsDisclaimer from '../ResultsDisclaimer'
import { formatR } from '../trackRecord/recordUI'
import CallReceipt from './CallReceipt'
import { isOpenSignal, pnlText } from './signalChips'

const DASH = '—'

const Row = ({ label, children, testId }: { label: string; children: ReactNode; testId: string }) => (
  <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 py-2" data-testid={testId}>
    <dt className="text-gray-400">{label}</dt>
    <dd className="text-right font-mono tabular-nums text-gray-100">{children}</dd>
  </div>
)

const OUTCOME_LABEL = { win: 'Win', loss: 'Loss', breakeven: 'Break-even' } as const

/**
 * The call as a record: when it was published, how it ended, what it made
 * before fees (percent and R), whether it counts, and its receipt. No net
 * figure: nothing a user sees assumes a fee.
 * Every row is present for every call, with a dash where it does not apply
 * yet, so an open call, a win, a loss and an excluded call read the same way.
 */
const CallRecordPanel = ({ call }: { call: PublicCall }) => {
  const signal = call.signal
  const record = call.record
  const open = signal ? isOpenSignal(signal) : true
  const reason = signal && !open ? exitReason(signal) : null
  const closedAt = !open ? formatUtcSeconds(signal?.closed_at) : null
  const fill = signal && !open ? fillMethodLabel(signal) : null

  return (
    <section
      className="mt-4 rounded-xl border border-terminal-border bg-terminal-surface p-4 md:p-6"
      aria-label="Call record"
      data-testid="call-record"
    >
      <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-400">Call record</h2>
      <dl className="mt-2 divide-y divide-terminal-border text-sm">
        <Row label="Published" testId="row-published">
          {formatUtcSeconds(call.published_at) ?? DASH}
        </Row>
        <Row label="Status" testId="row-status">
          {!signal ? 'Sealed until close' : open ? 'Open' : record?.outcome ? `Closed · ${OUTCOME_LABEL[record.outcome]}` : 'Closed'}
        </Row>
        <Row label="Closed" testId="row-closed">
          {closedAt ?? DASH}
        </Row>
        <Row label="Close reason" testId="row-reason">
          {reason ? EXIT_REASON_LABEL[reason] : DASH}
        </Row>
        <Row label="Close price" testId="row-close-price">
          {!open && signal?.exit_price != null ? `$${formatPrice(signal.exit_price)}` : DASH}
        </Row>
        {/* Closes under the level rule only; earlier closes read as recorded. */}
        {fill && (
          <Row label="Fill" testId="row-fill">
            {fill}
          </Row>
        )}
        <Row label="Result (before fees)" testId="row-gross">
          {record?.gross_pnl_pct != null ? (
            <span className={pnlText(record.gross_pnl_pct)}>{formatPnlPct(record.gross_pnl_pct)}</span>
          ) : (
            DASH
          )}
        </Row>
        <Row label="R (before fees)" testId="row-gross-r">
          {record?.gross_r != null ? <span className={pnlText(record.gross_r)}>{formatR(record.gross_r)}</span> : DASH}
        </Row>
        <Row label="In the track record" testId="row-excluded">
          {!signal ? (
            DASH
          ) : signal.excluded_from_record ? (
            <span className="text-amber-300">
              Excluded
              {signal.exclusion_note ? ` — ${signal.exclusion_note}` : ''}
              {record?.excluded_at ? ` (${formatUtcSeconds(record.excluded_at)})` : ''}
            </span>
          ) : (
            'Counts'
          )}
        </Row>
        <Row label="Receipt" testId="row-receipt">
          <CallReceipt receipt={call.receipt} />
        </Row>
      </dl>
      <p className="mt-3 text-sm">
        <Link to={`${VERIFY_PATH}?id=${call.id}`} className="text-accent hover:underline" data-testid="verify-call">
          Verify this call →
        </Link>
      </p>
      <ResultsDisclaimer className="mt-3" />
      <LevelExitsNote className="mt-1" />
      {/* Engine calls only: a creator's call is never decided by Phase 2. */}
      {!call.creator && signal && signal.trading_mode !== 'creator' && <Phase2TrialNote className="mt-1" />}
    </section>
  )
}

export default CallRecordPanel
